package no.vareflow.app.importer

/**
 * DEFLATE decompression (RFC 1951) in plain Kotlin, so .xlsx files (ZIP + deflate) can be read
 * the same way on the web and on Android. Raw deflate only (no zlib/gzip header).
 */
internal object Inflate {
    private val LENGTH_BASE = intArrayOf(3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131, 163, 195, 227, 258)
    private val LENGTH_EXTRA = intArrayOf(0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0)
    private val DIST_BASE = intArrayOf(1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049, 3073, 4097, 6145, 8193, 12289, 16385, 24577)
    private val DIST_EXTRA = intArrayOf(0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13)
    private val CL_ORDER = intArrayOf(16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15)

    class FormatException(message: String) : IllegalArgumentException(message)

    /** Canonical Huffman decoding table: counts per code length and symbols in code order. */
    private class Huffman(lengths: IntArray) {
        val counts = IntArray(16)
        val symbols: IntArray

        init {
            for (l in lengths) counts[l]++
            counts[0] = 0
            val offsets = IntArray(16)
            for (i in 1 until 16) offsets[i] = offsets[i - 1] + counts[i - 1]
            symbols = IntArray(lengths.size)
            for (s in lengths.indices) if (lengths[s] != 0) symbols[offsets[lengths[s]]++] = s
        }
    }

    private class Reader(val data: ByteArray, var pos: Int) {
        private var bitBuf = 0
        private var bitCount = 0

        fun bits(n: Int): Int {
            var value = bitBuf
            while (bitCount < n) {
                if (pos >= data.size) throw FormatException("Unexpected end of compressed data")
                value = value or ((data[pos++].toInt() and 0xFF) shl bitCount)
                bitCount += 8
            }
            bitBuf = value ushr n
            bitCount -= n
            return value and ((1 shl n) - 1)
        }

        fun alignToByte() { bitBuf = 0; bitCount = 0 }

        fun decode(h: Huffman): Int {
            var code = 0
            var first = 0
            var index = 0
            for (len in 1 until 16) {
                code = code or bits(1)
                val count = h.counts[len]
                if (code - count < first) return h.symbols[index + (code - first)]
                index += count
                first += count
                first = first shl 1
                code = code shl 1
            }
            throw FormatException("Bad Huffman code")
        }
    }

    private val FIXED_LIT: Huffman by lazy {
        val l = IntArray(288)
        for (i in 0..143) l[i] = 8
        for (i in 144..255) l[i] = 9
        for (i in 256..279) l[i] = 7
        for (i in 280..287) l[i] = 8
        Huffman(l)
    }
    private val FIXED_DIST: Huffman by lazy { Huffman(IntArray(30) { 5 }) }

    private class Output(size: Int) {
        var buf = ByteArray(size)
        var pos = 0
        fun ensure(extra: Int) {
            if (pos + extra <= buf.size) return
            var cap = buf.size * 2
            while (cap < pos + extra) cap *= 2
            buf = buf.copyOf(cap)
        }
    }

    private fun Reader.inflateBlockInto(o: Output, lit: Huffman, dist: Huffman) {
        while (true) {
            val sym = decode(lit)
            when {
                sym < 256 -> { o.ensure(1); o.buf[o.pos++] = sym.toByte() }
                sym == 256 -> return
                else -> {
                    val li = sym - 257
                    if (li >= 29) throw FormatException("Bad length symbol")
                    val len = LENGTH_BASE[li] + bits(LENGTH_EXTRA[li])
                    val di = decode(dist)
                    if (di >= 30) throw FormatException("Bad distance symbol")
                    val d = DIST_BASE[di] + bits(DIST_EXTRA[di])
                    if (d > o.pos) throw FormatException("Distance too far back")
                    o.ensure(len)
                    var from = o.pos - d
                    repeat(len) { o.buf[o.pos++] = o.buf[from++] }
                }
            }
        }
    }

    /** Decompresses raw deflate data starting at [offset]; [expectedSize] is a hint for the buffer. */
    fun inflate(data: ByteArray, offset: Int = 0, expectedSize: Int = 0): ByteArray {
        val o = Output(if (expectedSize > 0) expectedSize else maxOf(1024, (data.size - offset) * 4))
        fun inflateBlock(r: Reader, lit: Huffman, dist: Huffman) = r.inflateBlockInto(o, lit, dist)
        val r = Reader(data, offset)
        do {
            val last = r.bits(1)
            when (r.bits(2)) {
                0 -> {
                    r.alignToByte()
                    if (r.pos + 4 > data.size) throw FormatException("Truncated stored block")
                    val len = (data[r.pos].toInt() and 0xFF) or ((data[r.pos + 1].toInt() and 0xFF) shl 8)
                    r.pos += 4
                    if (r.pos + len > data.size) throw FormatException("Truncated stored block")
                    o.ensure(len)
                    data.copyInto(o.buf, o.pos, r.pos, r.pos + len)
                    o.pos += len
                    r.pos += len
                }
                1 -> inflateBlock(r, FIXED_LIT, FIXED_DIST)
                2 -> {
                    val hlit = r.bits(5) + 257
                    val hdist = r.bits(5) + 1
                    val hclen = r.bits(4) + 4
                    val clLengths = IntArray(19)
                    for (i in 0 until hclen) clLengths[CL_ORDER[i]] = r.bits(3)
                    val clTable = Huffman(clLengths)
                    val lengths = IntArray(hlit + hdist)
                    var i = 0
                    while (i < hlit + hdist) {
                        val sym = r.decode(clTable)
                        when {
                            sym < 16 -> lengths[i++] = sym
                            sym == 16 -> {
                                if (i == 0) throw FormatException("Repeat without previous length")
                                val prev = lengths[i - 1]
                                repeat(3 + r.bits(2)) { lengths[i++] = prev }
                            }
                            sym == 17 -> repeat(3 + r.bits(3)) { lengths[i++] = 0 }
                            else -> repeat(11 + r.bits(7)) { lengths[i++] = 0 }
                        }
                    }
                    inflateBlock(r, Huffman(lengths.copyOfRange(0, hlit)), Huffman(lengths.copyOfRange(hlit, hlit + hdist)))
                }
                else -> throw FormatException("Bad block type")
            }
        } while (last == 0)
        return o.buf.copyOf(o.pos)
    }
}
